import React from 'react';
import type {ButtonHTMLAttributes, CSSProperties, ImgHTMLAttributes, ReactNode} from 'react';
import {useAuth} from '../contexts/AuthContext.jsx';
import {sb} from '../lib/supabase.js';
import {createPrivateStorageSignedUrl, resolvePrivateStoragePath} from '../lib/privateStorage.js';
import {usePrivateStorageSignedUrl} from './privateStorageUrlData.js';

// `catch` binds `unknown` under strict mode, so the pre-existing
// `cause.message || fallback` idiom no longer compiles. This reproduces it
// EXACTLY — a truthy `.message` wins, anything else falls back — without
// asserting a shape onto a value that genuinely could be anything thrown.
function storageErrorText(cause: unknown, fallback: string): string {
  const message = typeof cause === 'object' && cause !== null ? (cause as {message?: unknown}).message : undefined;
  return typeof message === 'string' && message ? message : fallback;
}

const FALLBACK_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--surface-2)',
  color: 'var(--ink-faint)',
};

// A private-storage object is addressed EITHER by (bucket, path) for objects
// written since the private-bucket migration, OR by a legacy absolute URL for
// rows that predate it. resolvePrivateStoragePath() picks between them, so all
// three are individually optional and only their combination is meaningful —
// which is why this is three optional fields rather than a discriminated union
// that would claim a guarantee the runtime does not enforce.
type PrivateStorageRef = {
  // REQUIRED, unlike the other two. resolvePrivateStoragePath does a
  // `SIGNERS[bucket]` lookup and returns null for an unknown bucket, so a
  // missing bucket is not a "legacy URL only" case — it is a wiring error, and
  // the type says so.
  bucket: string;
  path?: unknown;
  legacyUrl?: unknown;
};

export function usePrivateStorageUrl(bucket: string, path?: unknown, legacyUrl?: unknown) {
  const {authState, rolePreview} = useAuth();
  // Automatic images are now a scoped Query concern. Keep this public hook's
  // return shape unchanged so every image presenter preserves its existing
  // fallback/error rendering while query identity, cancellation, and expiry
  // handling live in the shared owner.
  const state = usePrivateStorageSignedUrl({client: sb, authState, rolePreview, bucket, path, legacyUrl});
  return {
    ...state,
    error: state.error ? storageErrorText(state.error, 'Could not load file.') : null,
  };
}

/**
 * Authenticated thumbnail/full-image renderer. Signed URLs are fetched on
 * mount and never persisted into application records.
 */
export type PrivateStorageImageProps = PrivateStorageRef &
  Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> & {alt?: string; style?: CSSProperties};

export function PrivateStorageImage({bucket, path, legacyUrl, alt, style, ...imgProps}: PrivateStorageImageProps) {
  const {url, error, loading} = usePrivateStorageUrl(bucket, path, legacyUrl);
  if (!url) {
    return (
      <span
        role="img"
        aria-label={alt}
        aria-busy={loading ? 'true' : undefined}
        title={error || 'Authorizing image…'}
        style={{...FALLBACK_STYLE, ...style}}
      >
        {error ? '!' : '…'}
      </span>
    );
  }
  return <img {...imgProps} src={url} alt={alt} style={style} />;
}

/**
 * Button that mints a fresh URL at click time before opening a new tab. This
 * avoids stale links when a page has remained open beyond the thumbnail URL's
 * lifetime. Failures are surfaced inline instead of silently doing nothing.
 */
// EVERY PRESENTATION PROP IS OPTIONAL, AND THAT IS WHY THIS FILE WAS CONVERTED.
//
// While this module was JavaScript, TypeScript inferred these props from the
// destructuring pattern, and a destructured name with NO DEFAULT is REQUIRED.
// So `wrapperStyle`, `errorStyle` and `onError` were demanded from every
// TypeScript caller even though most legitimately omit them — which blocked
// seven components from converting. Optionality here is read off the real call
// sites, not assumed.
export type PrivateStorageOpenButtonProps = PrivateStorageRef &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'style' | 'children'> & {
    children?: ReactNode;
    style?: CSSProperties;
    wrapperStyle?: CSSProperties;
    errorStyle?: CSSProperties;
    onError?: (message: string) => void;
  };

export function PrivateStorageOpenButton({
  bucket,
  path,
  legacyUrl,
  children,
  style,
  wrapperStyle,
  errorStyle,
  onError,
  ...buttonProps
}: PrivateStorageOpenButtonProps) {
  const resolvedPath = resolvePrivateStoragePath(bucket, path, legacyUrl);
  const [opening, setOpening] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  async function open(event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    setError(null);
    setOpening(true);
    try {
      const url = await createPrivateStorageSignedUrl(sb, bucket, resolvedPath);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (cause: unknown) {
      const message = storageErrorText(cause, 'Could not open file.');
      setError(message);
      if (onError) onError(message);
    } finally {
      setOpening(false);
    }
  }

  return (
    <span style={{display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', ...wrapperStyle}}>
      <button
        {...buttonProps}
        type="button"
        onClick={open}
        disabled={buttonProps.disabled || opening || !resolvedPath}
        aria-busy={opening ? 'true' : undefined}
        style={style}
      >
        {opening ? 'Opening…' : children}
      </button>
      {error && (
        <span role="alert" style={{fontSize: 10, color: 'var(--danger)', marginTop: 3, ...errorStyle}}>
          {error}
        </span>
      )}
    </span>
  );
}

// NOT anchor attributes, despite the name: this renders a
// PrivateStorageOpenButton styled to look like a link, so its props are the
// BUTTON's. Typing it from <a> compiled until the spread hit the button and
// `type` collided ('string' vs the button's 'button'|'reset'|'submit') — the
// component's real element, not its name, decides the type.
export type PrivateStorageLinkProps = PrivateStorageOpenButtonProps;

export function PrivateStorageLink({children = 'View', style, ...props}: PrivateStorageLinkProps) {
  return (
    <PrivateStorageOpenButton
      {...props}
      style={{
        padding: 0,
        border: 'none',
        background: 'none',
        color: 'var(--brand)',
        cursor: 'pointer',
        fontFamily: 'inherit',
        textDecoration: 'none',
        ...style,
      }}
    >
      {children}
    </PrivateStorageOpenButton>
  );
}
